package com.eladfinish.bible.contest.screens.browse.usecases.chumash

import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase.Companion.OTIOT_PASUK_RELATIVE_SIZE
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.ColorConstants.INDIGO_DARK
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OTIOT
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.data.common.stringResolver.StringResolver
import com.eladfinish.bible.contest.data.common.stringResolver.resolveString
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.entities.Parashot
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.highlightTextSpannedRange
import com.eladfinish.bible.contest.parashat
import com.eladfinish.bible.contest.perek_str
import com.eladfinish.bible.contest.platform.dispatchers.DefaultDispatcher
import com.eladfinish.bible.contest.platform.dispatchers.IoDispatcher
import com.eladfinish.bible.contest.screens.browse.usecases.HaftarotUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.MakeOtiotAliyahSmallerUseCase
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.StringDesc
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.withContext
import kotlin.math.max

// make Shnaim Mikra Veechad Targum - שניים מקרא ואחד תרגום
class GetShmotUseCase(
    private val getContentForChumashModeUseCase: GetContentForChumashModeUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
    private val makeOtiotAliyahSmallerUseCase: MakeOtiotAliyahSmallerUseCase,
    private val getParashaNameSpannedUseCase: GetParashaNameSpannedUseCase,
    private val haftarotUseCase: HaftarotUseCase,
    private val stringResolver: StringResolver,
    private val assetReader: AssetReader,
    private val settings: Settings,
) {

    companion object {
        // Slightly reduce Targum size to visually differentiate it from Mikra.
        private const val TARGUM_RELATIVE_SIZE = 0.95f

        private const val TWO_SPACES = "  "
        private const val DOUBLE_NEWLINE = "\n\n"
        private const val PARASHA_SEPARATOR = "-"
        private const val ONQELOS_DIR = "onqelos"
        private const val ONQELOS_SUFFIX = "_onqelos.txt"
        private const val TARGUM_PREFIX_MARK = ") "
        private const val END_WITH_MIKRA_NOTICE = "[וחוזרים על הפסוק האחרון פעמיים כדי לסיים במקרא]"
    }

    suspend operator fun invoke(
        bookIndex: Int,
        partIndex: Int,
        parashaNameDesc: StringDesc,
        cachedParashot: Parashot,
    ): SpannableStringKMP {
        // CPU: reading Mikra content may involve parsing/formatting - keep it on Default.
        val mikra = withContext(DefaultDispatcher) {
            getContentForChumashModeUseCase(
                bookIndex = bookIndex,
                partIndex = partIndex,
                cachedParashot = cachedParashot,
                isTikunKorim = false,
            ).content
        }

        val chumashIndex = bookIndex - 1
        val targumFileName = buildTargumFileName(chumashIndex)

        // IO: file reading must not run on the main thread.
        val targumRaw = withContext(IoDispatcher) {
            assetReader.readFile(
                fileName = ONQELOS_DIR + assetReader.fileSeparator + targumFileName
            )
        }

        // CPU: all text manipulation/regex/string building.
        return withContext(DefaultDispatcher) {
            buildShmot(
                bookIndex = bookIndex,
                mikra = mikra,
                targumRaw = targumRaw,
                parashaNameDesc = parashaNameDesc,
            )
        }
    }

    private fun buildShmot(
        bookIndex: Int,
        mikra: String,
        targumRaw: String,
        parashaNameDesc: StringDesc,
    ): SpannableStringKMP {

        val parashaNameResolved = stringResolver.resolveString(parashaNameDesc)
        val (parashaForSearch, parashaForHaftarah) = splitParashaName(parashaNameResolved)

        val parashatLabel = stringResolver.resolveString(SharedRes.strings.parashat)
        val searchNeedle = "$parashatLabel $parashaForSearch"

        var startTargum = targumRaw.indexOf(searchNeedle)
        if (startTargum == -1) {
            Napier.d("$searchNeedle לא נמצאה")
            startTargum = 0
        }

        var endTargum = targumRaw.indexOf(parashatLabel, startTargum + 1)
        if (parashaNameResolved.contains(PARASHA_SEPARATOR)) {
            endTargum = targumRaw.indexOf(parashatLabel, endTargum + 1)
        }
        if (endTargum == -1) endTargum = targumRaw.length

        val targum = targumRaw.substring(startTargum, endTargum).trim()

        val mikraVerses = mikra.split(COLON_SPLITTER).filter { it.isNotEmpty() }
        val targumVerses = targum.split(COLON_SPLITTER).filter { it.isNotEmpty() }

        if (mikraVerses.size != targumVerses.size) {
            // Keep behavior: zip will cut to min size; log for diagnostics only.
            Napier.d("Warning: mikra/targum verses count mismatch. Using zip(minSize).")
        }

        val shmot = StringBuilder()
        val spannedRanges = mutableListOf<SpannedRange>()

        val perekLabel = stringResolver.resolveString(SharedRes.strings.perek_str)
        val perekRegex = "$perekLabel\\s$OTIOT\\s".toRegex()
        val aliyahRegex = "\\[.+]\\s".toRegex()
        val firstWhitespaceRegex = "\\s".toRegex()

        @Suppress("RegExpRedundantEscape")
        val aliyotRegex = "\\[.*?\\]\\s".toRegex()
        val otPasukRegex = "^$ALEF_TAV+\\s".toRegex()

        var lastMikraChunk = ""

        fun addTargumStyling(range: TextRange) {
            spannedRanges += SpannedRange(SpanType.RelativeSize(TARGUM_RELATIVE_SIZE), range)
            spannedRanges += SpannedRange(SpanType.ForegroundColor(INDIGO_DARK), range)
        }

        fun addPrefixDownscaleIfNeeded(prefix: String) {
            val trimmed = prefix.trim()
            if (trimmed != "פרק" && !trimmed.startsWith("[")) {
                spannedRanges += SpannedRange(
                    SpanType.RelativeSize(OTIOT_PASUK_RELATIVE_SIZE),
                    TextRange(shmot.length, shmot.length + trimmed.length),
                )
            }
        }

        fun String.removeAliyot(): String =
            replace(regex = aliyotRegex, replacement = "")

        fun String.removeOtPerekOrOtPasukRepeatedly(): String {
            var result = this
            while (otPasukRegex.containsMatchIn(input = result)) {
                result = result.replaceFirst(regex = otPasukRegex, replacement = "")
            }
            return result
        }

        mikraVerses.zip(targumVerses).forEach { (mikraVerse, targumVerse) ->
            var tempMikra = mikraVerse.trim()

            var indexPerek = 0
            var indexAliyah = 0

            perekRegex.find(tempMikra)?.let { indexPerek = it.end }
            aliyahRegex.find(tempMikra)?.let { indexAliyah = it.end }

            var prefixEnd = max(indexPerek, indexAliyah)
            firstWhitespaceRegex.find(tempMikra)?.let { prefixEnd = it.end }

            val prefix = tempMikra.take(prefixEnd)
            tempMikra = tempMikra.substring(prefixEnd)

            lastMikraChunk = tempMikra + COLON_SPLITTER + TWO_SPACES

            val targumBody = targumVerse.trim().substringAfter(TARGUM_PREFIX_MARK)
            val targumChunk = targumBody + COLON_SPLITTER

            addPrefixDownscaleIfNeeded(prefix)
            shmot.append(prefix)

            spannedRanges += SpannedRange(
                SpanType.Bold,
                TextRange(shmot.length, shmot.length + lastMikraChunk.length),
            )

            shmot.append(lastMikraChunk)
            shmot.append(
                lastMikraChunk
                    .removeAliyot()
                    .removeOtPerekOrOtPasukRepeatedly()
            )

            val targumRange = TextRange(shmot.length, shmot.length + targumChunk.length)
            addTargumStyling(targumRange)

            shmot.append(targumChunk)
            shmot.append(DOUBLE_NEWLINE)
        }

        shmot.append(END_WITH_MIKRA_NOTICE).append('\n')

        spannedRanges += SpannedRange(
            SpanType.Bold,
            TextRange(shmot.length, shmot.length + lastMikraChunk.length),
        )

        shmot.append(lastMikraChunk)
        shmot.append(lastMikraChunk)
        shmot.append(DOUBLE_NEWLINE)

        val haftarah = haftarotUseCase.parseHaftarot(
            parashaName = parashaForHaftarah,
            bookIndex = bookIndex,
            eachVerseInNewLine = settings.getBoolean(
                SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE,
                BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE,
            ),
            onlyQri = settings.getBoolean(
                SharedPrefsConstants.ONLY_QRI_SWITCH,
                true,
            ),
        )
        shmot.append(haftarah)

        val resultText = shmot.toString()

        val parashaNameSpans = parashaNameResolved
            .split(PARASHA_SEPARATOR)
            .map { it.trim() }
            .map { name -> getParashaNameSpannedUseCase.getParashaNameSpanned(resultText, name) }
            .map { (start, end) -> highlightTextSpannedRange(start, end) }

        spannedRanges += makeOtiotPasukSmallerUseCase(resultText)
        spannedRanges += makeOtiotAliyahSmallerUseCase(resultText)
        spannedRanges += parashaNameSpans

        return SpannableStringKMP(
            charSequence = resultText,
            spannedRangeList = spannedRanges.toImmutableList(),
        )
    }

    private fun buildTargumFileName(chumashIndex: Int): String {
        val bookEnglishName = BooksRepository.bookNamesEnglish[chumashIndex]
        return "_${chumashIndex + 1}_${bookEnglishName}$ONQELOS_SUFFIX"
    }

    private fun splitParashaName(name: String): Pair<String, String> {
        val index = name.indexOf(PARASHA_SEPARATOR)
        return if (index == -1) {
            name to name
        } else {
            name.take(index).trim() to name.substring(index + 1).trim()
        }
    }
}
